import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { first, firstValueFrom, Observable, Subscription } from 'rxjs';
import { Event } from '../../../../models/event.model';
import { Group } from '../../../../models/group.model';
import {
  MembershipStatus,
  MemberType,
} from '../../../../models/membership.status';
import { AuthService } from '../../../../services/auth.service';
import * as eventActions from '../../../../state/actions/event.actions';
import * as groupActions from '../../../../state/actions/group.actions';
import { AppState } from '../../../../state/appstate';
import * as eventSelector from '../../../../state/selectors/event.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { CalendarComponent } from '../../../components/calendar/calendar.component';

@Component({
  selector: 'app-list-event',
  standalone: true,
  imports: [
    RouterModule,
    AsyncPipe,
    BtnBackComponent,
    BtnRouteComponent,
    CalendarComponent,
    NgxSkeletonLoaderModule,
  ],
  providers: [MatDatepickerModule],
  templateUrl: './list-event.component.html',
  styleUrls: ['./list-event.component.css'],
})
export class ListEventComponent implements OnInit {
  route: ActivatedRoute = inject(ActivatedRoute);
  auth: AuthService = inject(AuthService);

  events$: Observable<any> = new Observable();
  eventsSub: Subscription | undefined;

  eventsLoading$: Observable<boolean> = new Observable();
  groupStatsLoading$: Observable<boolean> = new Observable();
  eventStatsLoading$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  group$: Observable<Group | null | undefined> = new Observable();

  // totalActiveEvents$: Observable<number>;

  viewDate: Date = new Date();

  myRoles$: Observable<any> = new Observable();

  membershipStatus = MembershipStatus;
  memberType = MemberType;

  constructor(
    private store: Store<AppState>,
    private router: Router,
  ) {
    if (this.groupId != null) {
      this.group$ = this.store.pipe(
        select(groupSelector.getGroupById(this.groupId!)),
      );

      this.events$ = this.store.pipe(
        select(eventSelector.getDateSortedEvents(this.groupId!, moment())),
      );

      this.myRoles$ = this.store.select(
        groupSelector.getMyRolesInGroup(this.groupId),
      );

      this.eventsLoading$ = this.store
        .select((state) => state.events)
        .pipe(select(eventSelector.loading));

      this.groupStatsLoading$ = this.store
        .select((state) => state.groups)
        .pipe(select(groupSelector.statsLoading));

      this.eventStatsLoading$ = this.store
        .select((state) => state.events)
        .pipe(select(eventSelector.eventStatsLoading));
    }
  }

  async ngOnInit(): Promise<void> {
    Promise.all([
      firstValueFrom(this.group$.pipe(first())),
      firstValueFrom(this.eventStatsLoading$.pipe(first())),
      firstValueFrom(this.events$.pipe(first())),
    ]).then(
      ([group, eventStatsLoading, events]: [
        Group | null | undefined,
        boolean,
        Event[] | null | undefined,
      ]) => {
        const isAuthenticated: boolean = this.auth.isAuthenticated();

        if (group?.adminMembers == undefined && isAuthenticated) {
          this.store.dispatch(
            groupActions.getGroupStats({ groupId: this.groupId! }),
          );
        }

        if (
          group?.activeEvents == undefined &&
          !eventStatsLoading &&
          isAuthenticated
        ) {
          this.store.dispatch(
            eventActions.getEventStats({ groupId: this.groupId! }),
          );
        }
        // console.log(
        //   `ListEventComponent :: ngOnInit :: Events length: ${events?.length}`
        // );
        if ((events?.length ?? 0) == 0 && isAuthenticated) {
          // console.log('ListEventComponent :: ngOnInit :: getEvents');
          this.getEvents(
            (group?.memberType as MemberType | undefined) ?? MemberType.member,
          );
        }
      },
    );
  }

  getEvents(memberType: MemberType, date?: moment.Moment) {
    this.store.dispatch(
      eventActions.getEvents({
        groupId: this.groupId!,
        memberType: memberType,
        startDate: Timestamp.fromDate(
          (date ?? moment()).startOf('month').toDate(),
        ),
        endDate: Timestamp.fromDate((date ?? moment()).endOf('month').toDate()),
      }),
    );
  }

  async onDateChange(date: moment.Moment) {
    const group: Group | null | undefined = await firstValueFrom(
      this.group$.pipe(first()),
    );
    this.events$ = this.store.pipe(
      select(eventSelector.getDateSortedEvents(this.groupId!, date)),
    );
    this.getEvents(
      (group?.memberType as MemberType | undefined) ?? MemberType.member,
      date,
    );
  }
}
